import QRCode from 'qrcode';

/**
 * The QR payload is fixed by the device-identity design (DEVICE_IDENTITY_ARCHITECTURE.md §2.1):
 * it is burned into the packaging, so changing the shape strands every printed unit.
 * Also readable as plain text by any scanner.
 */
const CLAIM_URL_BASE = 'https://autoaware.in/claim';

export function claimPayload(deviceUid: string, claimCode: string): string {
  return `${CLAIM_URL_BASE}?d=${encodeURIComponent(deviceUid)}&c=${encodeURIComponent(claimCode)}`;
}

export interface LabelDevice {
  device_uid: string;
  claim_code: string;
  /** Absent for secure-element units: their key never leaves the chip. */
  secret?: string | null;
}

/**
 * One row per unit: Device ID, claim code, device secret and an embedded QR image.
 *
 * The sheet carries the device secret (the QR itself does not: it encodes only the uid and
 * claim code). Whoever holds this file can forge that unit's telemetry, so keep it with the
 * flashing bench and do not send it, as is, to sticker printing.
 */
export async function buildClaimLabelsWorkbook(devices: LabelDevice[]): Promise<ArrayBuffer> {
  const { Workbook } = await import('exceljs');
  const wb = new Workbook();
  const ws = wb.addWorksheet('Labels');
  ws.columns = [
    { header: '#', key: 'n', width: 5 },
    { header: 'Device ID', key: 'uid', width: 30 },
    { header: 'Claim code', key: 'code', width: 30 },
    { header: 'Secret', key: 'secret', width: 50 },
    { header: 'QR', key: 'qr', width: 18 },
  ];
  ws.getRow(1).font = { bold: true };

  for (const [i, d] of devices.entries()) {
    const row = ws.addRow({
      n: i + 1,
      uid: d.device_uid,
      code: d.claim_code.match(/.{1,4}/g)?.join('-') ?? d.claim_code,
      secret: d.secret ?? '',
    });
    row.height = 95;
    row.alignment = { vertical: 'middle' };

    const dataUrl = await QRCode.toDataURL(claimPayload(d.device_uid, d.claim_code), {
      margin: 1,
      width: 240,
      errorCorrectionLevel: 'M',
    });
    const imageId = wb.addImage({ base64: dataUrl, extension: 'png' });
    ws.addImage(imageId, {
      tl: { col: 4.1, row: row.number - 1 + 0.1 },
      ext: { width: 110, height: 110 },
    });
  }

  return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
}

export async function downloadClaimLabels(devices: LabelDevice[], filename: string): Promise<void> {
  const buffer = await buildClaimLabelsWorkbook(devices);
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}
